import React, { useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import ModalGeneral from './modal-general';
import transformWord from '../../utils/transformWord';
import { usaMiel } from '../content/coop-config';

const ModalParcelas = ({
  isOpen,
  handleClose,
  onSubmit,
  isEdit = false,
  productores = [],
  parcela = null,
  cuenta_uuid,
  nombreProductor,
  etiquetas,
}) => {
  const [formData, setFormData] = useState({
    uuid_cuenta: '',
    codigo: '',
    nombre: '',
    superficie: '',
    plantas: '',
  });
  const [errors, setErrors] = useState({});

  const producerOptions = useMemo(() => {
    if (isEdit) {
      return [
        {
          uuid: parcela?.cuenta_uuid || '',
          value: parcela?.cuenta_uuid || '',
          id: parcela?.id_cuenta || '',
          nombre: parcela?.cuenta || 'Productor',
          label: parcela?.cuenta || 'Productor',
        },
      ];
    }
    return productores;
  }, [isEdit, productores, parcela]);

  const getProductorId = item => item.uuid;
  const getProductorName = item => item.nombre;

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const initialProductorId = isEdit
      ? String(parcela?.cuenta_uuid || '')
      : '';

    setFormData({
      uuid_cuenta: initialProductorId,
      nombre: parcela?.nombre || '',
      codigo: parcela?.codigo || '',
      superficie: parcela?.superficie || '',
      plantas: parcela?.plantas || '',
    });
    setErrors({});

    return () => {
      setFormData({
        uuid_cuenta: '',
        codigo: '',
        nombre: '',
        superficie: '',
        plantas: '',
      });
      setErrors({});
    }
  }, [isOpen, isEdit, parcela, productores]);

  const onClose = () => {
    setErrors({});
    handleClose();
  };

  const onChange = event => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    if (!String(formData.uuid_cuenta || '').trim()) {
      nextErrors.uuid_cuenta = 'El productor es requerido';
    }
    if (!String(formData.nombre || '').trim()) {
      nextErrors.nombre = 'El nombre de parcela es requerido';
    }
    if (!usaMiel() && !String(formData.superficie || '').trim()) {
      nextErrors.superficie = 'La superficie es requerida';
    }
    if (!String(formData.plantas || '').trim()) {
      nextErrors.plantas = 'El numero de plantas es requerido';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const payload = {
      codigo: String(formData.codigo),
      nombre: String(formData.nombre),
      superficie: Number(formData.superficie),
      plantas: Number(formData.plantas),
    };

    isEdit ? payload.id_cuenta = String(parcela?.id_cuenta)
      : payload.uuid_cuenta = String(formData.uuid_cuenta)

    if (typeof onSubmit === 'function') {
      onSubmit(payload, { isEdit, parcela });
      return;
    }

    handleClose();
  };

  return (
    <ModalGeneral
      show={isOpen}
      title={isEdit ? `Editar ${transformWord(etiquetas?.parcela, ['singular','lowercase'])}` : `Agregar ${transformWord(etiquetas?.parcela, ['singular','lowercase'])}`}
      onSave={handleSubmit}
      onClose={onClose}
      loading={false}>
      <div className="modal-body" style={{ overflowY: 'auto' }}>
        <div className="row my-3">
          <div className="col-12">
            <label htmlFor="uuid_cuenta" style={{ display: 'block', marginBottom: '6px', color: '#666' }}>
              Productor
            </label>
            {isEdit ? (
              <input
                id="uuid_cuenta"
                name="uuid_cuenta"
                value={parcela?.cuenta || ''}
                className="form-control"
                readOnly
                style={{ backgroundColor: '#F5F5F5' }}
              />
            ) : (
              <div style={{ width: '100%' }}>
                <Select
                  inputId="uuid_cuenta"
                  name="uuid_cuenta"
                  value={producerOptions.find(option => getProductorId(option) === formData.uuid_cuenta) || null}
                  onChange={option => {
                    onChange({ target: { name: 'uuid_cuenta', value: getProductorId(option) } });
                  }}
                  options={producerOptions}
                  getOptionValue={getProductorId}
                  getOptionLabel={getProductorName}
                  placeholder="Selecciona un productor"
                  isClearable
                  classNamePrefix="select"
                  styles={{
                    control: base => ({
                      ...base,
                      width: '100%',
                      border: `1px solid ${errors.uuid_cuenta ? '#D9534F' : '#DADADA'}`,
                      backgroundColor: '#FFFFFF',
                    })
                  }}
                  noOptionsMessage={() => 'No hay productores disponibles'}
                  isDisabled={producerOptions.length === 0}
                />
                {producerOptions.length === 0 && (
                  <small style={{ color: '#D9534F' }}>No hay productores disponibles para este ciclo.</small>
                )}
              </div>
            )}
            {errors.uuid_cuenta ? <small style={{ color: '#D9534F' }}>{errors.uuid_cuenta}</small> : null}
          </div>
        </div>

        <div className="row my-3">
          <div className="col-12">
            <label htmlFor="codigo" style={{ display: 'block', marginBottom: '6px', color: '#666' }}>
              Código (opcional)
            </label>
            <input
              id="codigo"
              name="codigo"
              value={formData.codigo}
              onChange={onChange}
              type="text"
              className="form-control"
              style={{ border: `1px solid ${errors.codigo ? '#D9534F' : '#DADADA'}` }}
            />
            {errors.codigo ? <small style={{ color: '#D9534F' }}>{errors.codigo}</small> : null}
          </div>
        </div>

        <div className="row my-3">
          <div className="col-12">
            <label htmlFor="nombre" style={{ display: 'block', marginBottom: '6px', color: '#666' }}>
              Nombre de {transformWord(etiquetas?.parcela, ['singular','lowercase'])}
            </label>
            <input
              id="nombre"
              name="nombre"
              value={formData.nombre}
              onChange={onChange}
              type="text"
              className="form-control"
              style={{ border: `1px solid ${errors.nombre ? '#D9534F' : '#DADADA'}` }}
            />
            {errors.nombre ? <small style={{ color: '#D9534F' }}>{errors.nombre}</small> : null}
          </div>
        </div>

        <div className="row my-3">
          <div className="col-12">
            <label htmlFor="superficie" style={{ display: 'block', marginBottom: '6px', color: '#666' }}>
              Superficie en hectáreas{usaMiel() ? ' (opcional)' : ''}
            </label>
            <input
              id="superficie"
              name="superficie"
              value={formData.superficie}
              onChange={onChange}
              type="number"
              min="0"
              step="0.01"
              className="form-control"
              style={{ border: `1px solid ${errors.superficie ? '#D9534F' : '#DADADA'}` }}
            />
            {errors.superficie ? <small style={{ color: '#D9534F' }}>{errors.superficie}</small> : null}
          </div>
        </div>

        <div className="row my-3">
          <div className="col-12">
            <label htmlFor="plantas" style={{ display: 'block', marginBottom: '6px', color: '#666' }}>
              Numero de {transformWord(etiquetas?.planta, ['plural','lowercase'])}
            </label>
            <input
              id="plantas"
              name="plantas"
              value={formData.plantas}
              onChange={onChange}
              type="number"
              min="0"
              step="1"
              className="form-control"
              style={{ border: `1px solid ${errors.plantas ? '#D9534F' : '#DADADA'}` }}
            />
            {errors.plantas ? <small style={{ color: '#D9534F' }}>{errors.plantas}</small> : null}
          </div>
        </div>
      </div>
    </ModalGeneral>
  );
};

export default ModalParcelas;
